/**
 * Single source of truth for MCP Registry condition-operator mappings.
 *
 * The condition-builder uses internal operator IDs ("equals", "in", …) while
 * the API uses numeric `conditionId` strings ("1", "2", …). This module keeps
 * the bidirectional mapping and the operator i18n keys in one place so the
 * payload writer, payload reader, and config hook cannot drift apart.
 */
import { type ConditionOption } from "@/components/ConditionBuilder/types";

/** Operator ID → API conditionId (write direction). */
export const OPERATOR_TO_CONDITION_ID: Record<string, string> = {
  contains: "1",
  equals: "2",
  greater_than: "3",
  less_than: "4",
  starts_with: "5",
  ends_with: "6",
  not_equals: "7",
  not_contains: "8",
  in: "9",
  is_in_session: "10",
  not_in: "11",
};

/** API conditionId → operator ID (read direction). Derived from the inverse. */
export const CONDITION_ID_TO_OPERATOR_ID: Record<string, string> =
  Object.fromEntries(
    Object.entries(OPERATOR_TO_CONDITION_ID).map(([opId, condId]) => [
      condId,
      opId,
    ]),
  );

/** Operator ID → i18n key. */
export const OPERATOR_I18N_KEY: Record<string, string> = {
  contains: "common.operators.contains",
  equals: "common.operators.equals",
  greater_than: "common.operators.greaterThan",
  less_than: "common.operators.lessThan",
  starts_with: "common.operators.startsWith",
  ends_with: "common.operators.endsWith",
  not_equals: "common.operators.notEquals",
  not_contains: "common.operators.notContains",
  in: "common.operators.in",
  is_in_session: "common.operators.isInSession",
  not_in: "common.operators.notIn",
};

/** Untranslated label per operator ID — used by the payload reader where no
 * i18n context is available. The reader's output is overwritten by the live
 * operator list as soon as the attribute resolves. */
export const OPERATOR_DEFAULT_NAME: Record<string, string> = {
  contains: "Contains",
  equals: "Equals",
  greater_than: "Greater Than",
  less_than: "Less Than",
  starts_with: "Starts With",
  ends_with: "Ends With",
  not_equals: "Not Equals",
  not_contains: "Not Contains",
  in: "In",
  is_in_session: "Is in session",
  not_in: "Not In",
};

/** ConditionOption form (`{ id, name }`) for read-side use. */
export const CONDITION_ID_TO_OPERATOR_OPTION: Record<string, ConditionOption> =
  Object.fromEntries(
    Object.entries(CONDITION_ID_TO_OPERATOR_ID).map(([condId, opId]) => [
      condId,
      { id: opId, name: OPERATOR_DEFAULT_NAME[opId] ?? opId },
    ]),
  );
